import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  TableActionsMenu,
  type DataTableColumn,
} from '@wundo/ui';
import { formatDate } from '@/utils/dateUtils.ts';
import { createOrUpdateTemplate, deleteTemplate } from '@/lib/broadcast.ts';
import type { CreateTemplateRequest, Template } from '@/types/broadcast.ts';
import { TemplateEditorDialog } from './TemplateEditorDialog.tsx';
import type { TemplatesQuery } from './useBroadcastTemplates.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';

export interface TemplatesTabProps {
  templates: TemplatesQuery;
}

/** The Preview column: enough of the body to recognise it by. */
function preview(body: string): string {
  return body.length > 50 ? `${body.substring(0, 50)}...` : body;
}

/**
 * Every template, and the editor that writes them.
 *
 * The same list feeds the Send tab, so a save here changes what can be sent
 * without either tab reloading the other.
 */
export function TemplatesTab({ templates }: TemplatesTabProps) {
  const queryClient = useQueryClient();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Template | null>(null);
  const [saveError, setSaveError] = useState('');
  const [deleting, setDeleting] = useState<Template | null>(null);
  const [deleteError, setDeleteError] = useState('');

  const save = useMutation({
    mutationFn: (request: CreateTemplateRequest) => createOrUpdateTemplate(request),
    onSuccess: async () => {
      setEditorOpen(false);
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (failure) =>
      setSaveError(
        apiErrorMessage(
          failure,
          editing ? 'Failed to update the template' : 'Failed to create the template',
        ),
      ),
  });

  const remove = useMutation({
    mutationFn: (template: Template) => deleteTemplate(template.name),
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (failure) =>
      setDeleteError(apiErrorMessage(failure, 'Failed to delete the template')),
  });

  function openEditor(template: Template | null) {
    setEditing(template);
    setSaveError('');
    setEditorOpen(true);
  }

  const columns: DataTableColumn<Template>[] = [
    { key: 'name', header: 'Template Name' },
    {
      key: 'language',
      header: 'Language',
      nowrap: true,
      render: (template) => template.language.toUpperCase(),
    },
    {
      key: 'body',
      header: 'Preview',
      render: (template) => preview(template.body),
    },
    {
      key: 'created_at',
      header: 'Created',
      nowrap: true,
      render: (template) => formatDate(template.created_at),
    },
  ];

  return (
    <div className='flex flex-col gap-4'>
      <DataTable
        label='Templates'
        minWidth='lg'
        columns={columns}
        rows={templates.templates}
        rowKey={(template) => template.id}
        onRowClick={(template) => openEditor(template)}
        loading={templates.isPending}
        error={templates.isError ? 'Failed to load the templates' : undefined}
        pagination={{
          page: templates.page,
          pageSize: templates.pageSize,
          total: templates.total,
        }}
        onPageChange={templates.setPage}
        emptyState={
          <EmptyState
            title='No templates'
            description='Create one before sending a broadcast.'
          />
        }
        toolbar={
          <Button size='sm' onClick={() => openEditor(null)}>
            Create Template
          </Button>
        }
        actions={(template) => (
          <TableActionsMenu
            label={`Actions for ${template.name}`}
            items={[
              {
                label: 'Edit',
                icon: <Pencil aria-hidden='true' />,
                onSelect: () => openEditor(template),
              },
              {
                label: 'Delete',
                icon: <Trash2 aria-hidden='true' />,
                tone: 'danger',
                onSelect: () => {
                  setDeleteError('');
                  setDeleting(template);
                },
              },
            ]}
          />
        )}
      />

      <TemplateEditorDialog
        open={editorOpen}
        onOpenChange={(open) => {
          setEditorOpen(open);
          if (!open) {
            setEditing(null);
            setSaveError('');
          }
        }}
        template={editing}
        saving={save.isPending}
        error={saveError}
        onSave={(request) => {
          setSaveError('');
          save.mutate(request);
        }}
      />

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleting(null);
            setDeleteError('');
          }
        }}
        title='Delete Template'
        description={`Are you sure you want to delete the template "${deleting?.name ?? ''}"? This action cannot be undone.`}
        confirmLabel='Delete'
        cancelLabel='Cancel'
        variant='danger'
        error={deleteError}
        onConfirm={async () => {
          if (deleting === null) return;
          setDeleteError('');
          await remove.mutateAsync(deleting).catch(() => undefined);
        }}
      />
    </div>
  );
}
